import {
  DEFAULT_REFERENCE_TAG_RADIUS,
  MAX_REFERENCE_TAG_RADIUS,
  MIN_REFERENCE_TAG_RADIUS,
  createReferenceTag,
  sameReferenceTarget,
  summarizeReferenceTags,
  type ReferenceTag,
  type ReferenceTagSummary,
  type ReferenceTagTarget,
  type SuggestTagsResult,
} from '@/reference';
import {
  MAX_TAG_LABEL_LENGTH,
  MAX_TAG_NOTE_LENGTH,
  TAG_CATEGORIES,
  TAG_CATEGORY_INFO,
  TAG_EMPHASES,
  compareTags,
  tagCategoryInfo,
  type TagCategory,
  type TagEmphasis,
} from '@/semantics/SemanticTag';
import { append, clear, el } from './dom';
import { icon } from './Icons';
import { dialogButton } from './Dialog';

/**
 * Tagging the reference photograph — the step that makes stage 1 worth trusting.
 *
 * ## The same tool, pointed at a picture
 *
 * This is `TagBubble` and `TagTool` with the scene taken away. Click the photograph, a marker lands
 * where you clicked, and a panel underneath offers the same six categories, the same three emphases
 * and the same label-and-note pair — because it *is* the same act, and a designer who has tagged a
 * bench in the viewport should recognise this immediately. The class names are shared with `tag.css`
 * for the same reason: two surfaces that behave alike should look alike.
 *
 * ## One thing a click can mean
 *
 * A click marks a place. There is no second scope: the panel used to ask whether each tag was about
 * a spot or about the whole photograph, which put a decision in front of every tag and collected
 * material that already had a home — anything true of the site as a whole belongs in the direction
 * box, which every stage reads and which needs no marker.
 *
 * ## Suggested first, corrected second
 *
 * Nothing here is required, and an empty panel is the state this step actually failed in — not
 * because tagging is hard but because starting is. So the panel can fill itself in: one cheap call
 * comes back with a dozen labelled circles, and the work becomes correcting a draft rather than
 * composing one. Suggestions arrive visibly marked as suggestions, and stay marked in the panel and
 * in the prompt until someone edits one — see `reference/ReferenceTagSuggest.ts` for why that
 * distinction is load-bearing rather than decorative.
 *
 * Every tag is editable in place for the same reason: a suggestion you cannot fix is a suggestion
 * you have to delete and retype.
 *
 * ## Ownership
 *
 * The tagger owns its tag list. The host reads {@link tags} when it builds a prompt and is told
 * through `onChange` when the list has moved. `root` is a stable element across the tagger's whole
 * life, so a host that rebuilds its own tree on every render can re-append it without losing what is
 * typed — provided it renders on the `onChange` *before* this class re-renders itself, which is the
 * order used here. Typing inside a field notifies on commit rather than per keystroke, because a
 * host re-render moves this panel in the DOM and takes the caret with it.
 */

export interface ReferenceTaggerDeps {
  /** Called after any change to the tag list, before this panel redraws itself. */
  onChange?: () => void;
  /**
   * Ask a model for a first pass at the tags. Absent when the host cannot make calls at all.
   *
   * The tagger does not know what an API key is: the host owns the key, the model, the images and
   * the direction box, and hands back only the result.
   */
  suggest?: (signal: AbortSignal) => Promise<SuggestTagsResult>;
  /** Why suggesting is unavailable right now — no photograph, no key — or null when it can run. */
  suggestBlockedReason?: () => string | null;
}

export class ReferenceTagger {
  readonly root: HTMLElement;

  private readonly entries: ReferenceTag[] = [];
  private photoUrl: string | null = null;
  private photoName = 'Reference photograph';

  /** A place clicked but not yet named. Null once it has been committed or abandoned. */
  private pending: ReferenceTagTarget | null = null;
  /** The tag whose marker is highlighted and whose reach the slider moves. */
  private selectedId: string | null = null;

  private draftCategory: TagCategory = 'identity';
  private draftEmphasis: TagEmphasis = 'mention';
  private draftLabel = '';
  private draftNote = '';

  private suggesting = false;
  private suggestError: string | null = null;
  private suggestNotes: string | null = null;
  private suggestAbort: AbortController | null = null;

  /**
   * True while the reach slider is being dragged — a rebuild mid-drag tears the slider out of the
   * DOM and the drag dies after one frame. Same fix as `TagBubble`.
   */
  private dragging = false;
  private readonly onPointerUp = (): void => {
    if (!this.dragging) return;
    this.dragging = false;
    // Held until the drag ends: telling the host mid-drag makes it re-render, which moves this
    // panel in the DOM and takes the slider out from under the pointer.
    this.deps.onChange?.();
    this.render();
  };

  constructor(private readonly deps: ReferenceTaggerDeps = {}) {
    this.root = el('div', {
      class: 'lg-reftag',
      attrs: { 'aria-label': 'Tag the reference photograph' },
    });
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
    this.render();
  }

  /** The tags, loudest first — what a prompt builder should be handed. */
  get tags(): ReferenceTag[] {
    return [...this.entries].sort(compareTags);
  }

  get summary(): ReferenceTagSummary {
    return summarizeReferenceTags(this.entries);
  }

  /**
   * Point the tagger at a photograph.
   *
   * Tags are kept when the same image comes back (a host re-render), and dropped when it changes —
   * a coordinate placed on one photograph means nothing on another, and a stale marker sitting over
   * a different garden is worse than no marker at all.
   */
  setPhoto(url: string | null, name?: string): void {
    if (url === this.photoUrl) return;
    this.photoUrl = url;
    this.photoName = name ?? 'Reference photograph';
    this.entries.length = 0;
    this.pending = null;
    this.selectedId = null;
    this.suggestError = null;
    this.suggestNotes = null;
    this.deps.onChange?.();
    this.render();
  }

  dispose(): void {
    this.suggestAbort?.abort();
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
    this.root.remove();
  }

  /* --------------------------------- Rendering --------------------------------- */

  private render(): void {
    clear(this.root);
    append(this.root, this.intro(), this.frame(), this.editor());
  }

  /**
   * What tagging buys, and the button that starts it for you.
   *
   * A strip rather than a checklist. The checklist that used to live here gated the run, and the
   * gate had to go — but the reason behind it did not, so the strip still says plainly what the tags
   * are worth. It is the one place in this panel that argues for the work rather than collecting it.
   */
  private intro(): HTMLElement {
    const summary = this.summary;
    const blocked = this.deps.suggestBlockedReason?.() ?? null;

    const suggest = dialogButton({
      label: this.suggesting ? 'Reading the photograph…' : summary.total > 0 ? 'Suggest more' : 'Suggest tags',
      icon: 'plant',
      primary: summary.total === 0,
      title:
        blocked ??
        'One cheap call marks what it can see, for you to correct. Suggestions stay marked as ' +
          'suggestions until you edit them.',
      onClick: () => void this.runSuggest(),
      disabled: this.suggesting || blocked !== null || this.deps.suggest === undefined,
    });

    return el(
      'div',
      { class: 'lg-reftag__intro', dataset: { tagged: String(summary.total > 0) } },
      el(
        'div',
        { class: 'lg-reftag__introtext' },
        el('span', { class: 'lg-reftag__introhead' }, el('span', { html: icon('tag', 14) }), el('span', {
          text: summary.total === 0 ? 'Optional — and worth more than any re-run' : `${summary.total} tagged`,
        })),
        el('span', {
          class: 'lg-reftag__introbody',
          text:
            summary.total === 0
              ? 'Stage 1 has the photograph and nothing about what it is of, so it reads a bin store as a fire pit and a paddock as a lawn. Naming a few things here is the cheapest correction in the pipeline.'
              : summary.suggested > 0
                ? `${summary.suggested} came from the automatic pass and are marked as unconfirmed — edit one and it becomes yours. Every stage reads all of them.`
                : 'Every stage reads these, with the coordinates you placed them on.',
        }),
        this.suggestError !== null && el('span', { class: 'lg-reftag__introerror', text: this.suggestError }),
        this.suggestNotes !== null &&
          el('span', {
            class: 'lg-reftag__intronote',
            text: `The pass said: ${this.suggestNotes}`,
          }),
      ),
      suggest,
    );
  }

  /**
   * The photograph, with a marker per tag.
   *
   * Markers are positioned HTML rather than SVG: their size is a fraction of the image *width* with a
   * square aspect, so a ring stays a ring on a panoramic frame — which the pipeline overlay's
   * `preserveAspectRatio="none"` viewBox cannot promise, and which matters more here because these
   * shapes are the user's own work rather than a stage's claim about the world.
   */
  private frame(): HTMLElement {
    if (!this.photoUrl) {
      return el('div', {
        class: 'lg-reftag__empty',
        text: 'Add a reference photograph, then tag what is in it.',
      });
    }

    const frame = el(
      'div',
      {
        class: 'lg-reftag__frame',
        on: {
          click: (event) => this.handleClick(event as MouseEvent),
        },
      },
      el('img', { attrs: { src: this.photoUrl, alt: this.photoName, draggable: 'false' } }),
    );

    for (const [index, tag] of this.tags.entries()) {
      const color = tagCategoryInfo(tag.category).color;
      frame.appendChild(
        el(
          'div',
          {
            class: `lg-reftag__pin${tag.id === this.selectedId ? ' is-active' : ''}${
              tag.origin === 'suggested' ? ' is-suggested' : ''
            }`,
            dataset: { tag: tag.id },
            attrs: { style: `--tag-color:${color}` },
            style: { left: `${tag.target.u * 100}%`, top: `${tag.target.v * 100}%` },
          },
          el(
            'span',
            { class: 'lg-reftag__ring', style: { width: `${tag.target.radius * 200}%` } },
            el('span', { class: 'lg-reftag__ringnum', text: String(index + 1) }),
          ),
          el('span', { class: 'lg-reftag__pinlabel', text: tag.label, title: tag.note ?? tag.label }),
        ),
      );
    }

    // The pending spot has no tag on it yet, so it needs its own mark — otherwise clicking the photo
    // appears to do nothing until the first label is typed.
    const pending = this.pending;
    if (pending) {
      frame.appendChild(
        el(
          'div',
          {
            class: 'lg-reftag__pin is-pending',
            style: { left: `${pending.u * 100}%`, top: `${pending.v * 100}%` },
          },
          el('span', { class: 'lg-reftag__ring', style: { width: `${pending.radius * 200}%` } }),
        ),
      );
    }

    return el(
      'div',
      { class: 'lg-reftag__stage' },
      frame,
      el('p', {
        class: 'lg-hint lg-reftag__frameHint',
        text: pending
          ? 'Marked. Say what is there below — or click somewhere else in the photograph.'
          : 'Click anything in the photograph to tag it: the house, the drive, the bed you want kept. Click a marker to edit it.',
      }),
    );
  }

  private handleClick(event: MouseEvent): void {
    const image = this.root.querySelector<HTMLImageElement>('.lg-reftag__frame img');
    if (!image) return;
    const rect = image.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    const u = clamp((event.clientX - rect.left) / rect.width, 0, 1);
    const v = clamp((event.clientY - rect.top) / rect.height, 0, 1);

    // Clicking an existing marker selects it for editing rather than stacking a second tag on top.
    const hit = this.tags.find(
      (tag) =>
        Math.hypot(tag.target.u - u, (tag.target.v - v) * (rect.height / rect.width)) <=
        tag.target.radius,
    );

    if (hit) {
      this.pending = null;
      this.selectedId = hit.id;
      this.render();
      this.root.querySelector<HTMLInputElement>(`[data-edits="${hit.id}"]`)?.focus();
      return;
    }

    this.pending = { u: round3(u), v: round3(v), radius: DEFAULT_REFERENCE_TAG_RADIUS };
    this.selectedId = null;
    this.render();
    this.focusDraft();
  }

  /* ---------------------------------- Editor ---------------------------------- */

  private editor(): HTMLElement {
    const body = el('div', { class: 'lg-reftag__editor' });
    append(body, this.reachControl(), this.draft(), this.tagList());
    return body;
  }

  /**
   * How much of the frame the marker in hand claims.
   *
   * Follows the selection — the pending spot while one is being named, the selected tag otherwise —
   * because reach describes the place rather than any one statement about it.
   */
  private reachControl(): HTMLElement | null {
    const target = this.pending ?? this.selected?.target ?? null;
    if (!target) return null;

    const readout = el('span', {
      class: 'lg-field__value',
      text: `${Math.round(target.radius * 100)}% of the width`,
    });
    const input = el('input', {
      class: 'lg-slider',
      attrs: {
        type: 'range',
        min: String(MIN_REFERENCE_TAG_RADIUS),
        max: String(MAX_REFERENCE_TAG_RADIUS),
        step: '0.005',
        value: String(target.radius),
        'aria-label': 'How much of the photograph this tag covers',
        'data-autofocus': 'skip',
      },
      on: {
        pointerdown: () => {
          this.dragging = true;
        },
        input: (event) => {
          const radius = Number((event.target as HTMLInputElement).value);
          readout.textContent = `${Math.round(radius * 100)}% of the width`;
          this.resize(radius);
        },
      },
    });

    return el(
      'div',
      { class: 'lg-control lg-tagger__radius' },
      el(
        'div',
        { class: 'lg-control__head' },
        el('span', { class: 'lg-field__label', text: this.pending ? 'Reach of the new tag' : 'Reach' }),
        readout,
      ),
      input,
    );
  }

  /** The form that names a freshly clicked place. Present only while there is one to name. */
  private draft(): HTMLElement | null {
    if (!this.pending) return null;

    const categories = el('div', { class: 'lg-tagger__cats' });
    for (const info of TAG_CATEGORY_INFO) {
      categories.appendChild(
        el('button', {
          class: `lg-tagger__cat${this.draftCategory === info.id ? ' is-active' : ''}`,
          text: info.name,
          title: `${info.hint}\n\ne.g. “${info.example}”`,
          attrs: {
            type: 'button',
            'aria-pressed': this.draftCategory === info.id,
            'data-autofocus': 'skip',
            style: `--tag-color:${info.color}`,
          },
          on: {
            click: () => {
              this.draftCategory = info.id;
              this.render();
              this.focusDraft();
            },
          },
        }),
      );
    }

    const label = el('input', {
      class: 'lg-input lg-tagger__label',
      attrs: {
        type: 'text',
        value: this.draftLabel,
        maxlength: String(MAX_TAG_LABEL_LENGTH),
        placeholder: tagCategoryInfo(this.draftCategory).example,
        'aria-label': 'Tag',
        spellcheck: 'true',
      },
      on: {
        input: (event) => {
          this.draftLabel = (event.target as HTMLInputElement).value;
        },
        keydown: (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.commit();
          }
        },
      },
    });

    const note = el('input', {
      class: 'lg-input lg-tagger__note',
      attrs: {
        type: 'text',
        value: this.draftNote,
        maxlength: String(MAX_TAG_NOTE_LENGTH),
        placeholder: 'Optional detail the photograph cannot show…',
        'aria-label': 'Tag detail',
        spellcheck: 'true',
        'data-autofocus': 'skip',
      },
      on: {
        input: (event) => {
          this.draftNote = (event.target as HTMLInputElement).value;
        },
        keydown: (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.commit();
          }
        },
      },
    });

    return el(
      'div',
      { class: 'lg-tagger__editor' },
      categories,
      label,
      note,
      el(
        'div',
        { class: 'lg-tagger__foot' },
        this.emphasisRow(this.draftEmphasis, (emphasis) => {
          this.draftEmphasis = emphasis;
          this.render();
          this.focusDraft();
        }),
        el('span', { class: 'lg-tagger__spacer' }),
        dialogButton({
          label: 'Discard',
          title: 'Forget this marker',
          onClick: () => {
            this.pending = null;
            this.render();
          },
        }),
        dialogButton({
          label: 'Add tag',
          primary: true,
          icon: 'tag',
          title: 'Add this tag  ·  Enter',
          onClick: () => this.commit(),
        }),
      ),
    );
  }

  /**
   * Every tag, editable where it stands.
   *
   * Editable rather than read-only because most rows here were written by a model: a suggestion you
   * can only delete and retype is barely better than no suggestion. Fields commit on blur rather
   * than per keystroke — the host re-renders on `onChange`, which moves this panel in the DOM and
   * takes the caret with it, so a keystroke-level notification would fight every word typed.
   */
  private tagList(): HTMLElement | null {
    const tags = this.tags;
    if (tags.length === 0) return null;

    const list = el('div', { class: 'lg-reftag__list' });
    for (const [index, tag] of tags.entries()) {
      const info = tagCategoryInfo(tag.category);

      const category = el('select', {
        class: 'lg-input lg-reftag__tagcat',
        attrs: { 'aria-label': `What kind of statement “${tag.label}” is`, 'data-autofocus': 'skip' },
        on: {
          change: (event) => {
            this.edit(tag.id, (entry) => {
              entry.category = (event.target as HTMLSelectElement).value as TagCategory;
            });
          },
        },
      });
      for (const option of TAG_CATEGORIES) {
        category.appendChild(
          el('option', {
            text: tagCategoryInfo(option).name,
            attrs: { value: option, selected: option === tag.category },
          }),
        );
      }
      category.value = tag.category;

      const label = el('input', {
        class: 'lg-input lg-reftag__taglabel',
        attrs: {
          type: 'text',
          value: tag.label,
          maxlength: String(MAX_TAG_LABEL_LENGTH),
          'aria-label': 'Tag',
          spellcheck: 'true',
          'data-autofocus': 'skip',
          'data-edits': tag.id,
        },
        on: {
          // The marker follows the typing without a re-render, so the photograph and the row never
          // disagree while a word is half-written.
          input: (event) => {
            const value = (event.target as HTMLInputElement).value;
            const entry = this.entries.find((candidate) => candidate.id === tag.id);
            if (entry) entry.label = value;
            const pin = this.root.querySelector<HTMLElement>(
              `.lg-reftag__pin[data-tag="${tag.id}"] .lg-reftag__pinlabel`,
            );
            if (pin) pin.textContent = value;
          },
          change: () => this.edit(tag.id, () => {}),
          blur: () => this.edit(tag.id, () => {}),
        },
      });

      const note = el('input', {
        class: 'lg-input lg-reftag__tagnote',
        attrs: {
          type: 'text',
          value: tag.note ?? '',
          maxlength: String(MAX_TAG_NOTE_LENGTH),
          placeholder: 'Detail the photograph cannot show…',
          'aria-label': 'Tag detail',
          spellcheck: 'true',
          'data-autofocus': 'skip',
        },
        on: {
          input: (event) => {
            const value = (event.target as HTMLInputElement).value.trim();
            const entry = this.entries.find((candidate) => candidate.id === tag.id);
            if (!entry) return;
            if (value.length > 0) entry.note = value;
            else delete entry.note;
          },
          change: () => this.edit(tag.id, () => {}),
          blur: () => this.edit(tag.id, () => {}),
        },
      });

      list.appendChild(
        el(
          'div',
          {
            class: `lg-reftag__tag${tag.id === this.selectedId ? ' is-active' : ''}`,
            dataset: { origin: tag.origin },
            attrs: { style: `--tag-color:${info.color}` },
            on: {
              // Selecting from the list highlights the marker, which is how a row and a place in the
              // photograph are tied together at all.
              pointerdown: () => {
                if (this.selectedId === tag.id) return;
                this.selectedId = tag.id;
                this.pending = null;
                this.render();
                this.root.querySelector<HTMLInputElement>(`[data-edits="${tag.id}"]`)?.focus();
              },
            },
          },
          el('span', { class: 'lg-reftag__tagnum', text: String(index + 1) }),
          el(
            'div',
            { class: 'lg-reftag__tagfields' },
            el('div', { class: 'lg-reftag__tagrow' }, category, label),
            note,
            el(
              'div',
              { class: 'lg-reftag__tagfoot' },
              this.emphasisRow(tag.emphasis, (emphasis) =>
                this.edit(tag.id, (entry) => {
                  entry.emphasis = emphasis;
                }),
              ),
              tag.origin === 'suggested' &&
                el('span', {
                  class: 'lg-reftag__badge',
                  text: 'suggested',
                  title:
                    'Read off the photograph by the automatic pass and not confirmed by you. Every ' +
                    'stage is told so. Editing this tag makes it yours.',
                }),
            ),
          ),
          el('button', {
            class: 'lg-tagchip__x',
            html: icon('close', 12),
            title: 'Remove this tag',
            attrs: { type: 'button', 'aria-label': `Remove ${tag.label}`, 'data-autofocus': 'skip' },
            on: { click: () => this.remove(tag.id) },
          }),
        ),
      );
    }
    return list;
  }

  private emphasisRow(current: TagEmphasis, onPick: (emphasis: TagEmphasis) => void): HTMLElement {
    const row = el('div', { class: 'lg-tagger__emphasis' });
    for (const emphasis of TAG_EMPHASES) {
      row.appendChild(
        el('button', {
          class: `lg-tagger__level${current === emphasis ? ' is-active' : ''}`,
          text: EMPHASIS_LABEL[emphasis],
          title: EMPHASIS_HINT[emphasis],
          attrs: { type: 'button', 'aria-pressed': current === emphasis, 'data-autofocus': 'skip' },
          on: { click: () => onPick(emphasis) },
        }),
      );
    }
    return row;
  }

  /* --------------------------------- Mutation --------------------------------- */

  private get selected(): ReferenceTag | null {
    return this.entries.find((tag) => tag.id === this.selectedId) ?? null;
  }

  private commit(): void {
    const target = this.pending;
    if (!target || this.draftLabel.trim().length === 0) {
      this.focusDraft();
      return;
    }
    const tag = createReferenceTag({
      target,
      label: this.draftLabel,
      category: this.draftCategory,
      emphasis: this.draftEmphasis,
      ...(this.draftNote.trim().length > 0 ? { note: this.draftNote } : {}),
    });
    this.entries.push(tag);
    this.draftLabel = '';
    this.draftNote = '';
    this.pending = null;
    this.selectedId = tag.id;
    this.changed();
  }

  /**
   * Apply an edit, and take the tag's authorship with it.
   *
   * A suggestion someone has changed is no longer a suggestion — they have looked at it, which is
   * the whole difference between a guess and a statement, and the prompt stops hedging it.
   */
  private edit(id: string, change: (tag: ReferenceTag) => void): void {
    const tag = this.entries.find((entry) => entry.id === id);
    if (!tag) return;
    change(tag);
    tag.origin = 'designer';
    this.selectedId = id;
    this.changed();
  }

  private remove(id: string): void {
    const index = this.entries.findIndex((tag) => tag.id === id);
    if (index < 0) return;
    this.entries.splice(index, 1);
    if (this.selectedId === id) this.selectedId = null;
    this.changed();
  }

  /** Move whichever marker is in hand — the pending spot, or the selected tag. */
  private resize(radius: number): void {
    if (this.pending) this.pending = { ...this.pending, radius };
    else {
      const tag = this.selected;
      if (!tag) return;
      tag.target = { ...tag.target, radius };
    }

    // The marker is the point of the control, so it has to move while the slider does — but by
    // touching the ring that is already on screen, not by rebuilding the panel around the pointer.
    // Neither a re-render nor an `onChange` happens until `onPointerUp`.
    for (const ring of this.root.querySelectorAll<HTMLElement>(
      '.lg-reftag__pin.is-active .lg-reftag__ring, .lg-reftag__pin.is-pending .lg-reftag__ring',
    )) {
      ring.style.width = `${radius * 200}%`;
    }
  }

  /* -------------------------------- Suggestions -------------------------------- */

  /**
   * Ask for a first pass, and merge what comes back.
   *
   * Merged rather than replacing: pressing the button a second time on a half-corrected panel must
   * not throw away the corrections. Anything landing on a place that is already tagged is dropped,
   * since a second opinion stacked invisibly under the first is worse than no second opinion.
   */
  private async runSuggest(): Promise<void> {
    const ask = this.deps.suggest;
    if (!ask || this.suggesting) return;

    this.suggesting = true;
    this.suggestError = null;
    this.suggestNotes = null;
    this.suggestAbort = new AbortController();
    this.render();

    try {
      const result = await ask(this.suggestAbort.signal);
      let added = 0;
      for (const tag of result.tags) {
        if (this.entries.some((entry) => sameReferenceTarget(entry.target, tag.target))) continue;
        this.entries.push(tag);
        added += 1;
      }
      this.suggestNotes = result.notes ?? null;
      if (added === 0) {
        this.suggestError = 'Nothing new came back from that pass.';
      }
    } catch (error) {
      this.suggestError =
        error instanceof DOMException && error.name === 'AbortError'
          ? 'Cancelled.'
          : `Could not suggest tags — ${describeError(error)}`;
    } finally {
      this.suggesting = false;
      this.suggestAbort = null;
      this.changed();
    }
  }

  /**
   * Tell the host first, redraw second.
   *
   * A host that re-renders on `onChange` will move `root` in the DOM, which blurs whatever is focused
   * inside it. Doing that before this panel rebuilds means the field the user is typing into is the
   * one created last.
   */
  private changed(): void {
    this.deps.onChange?.();
    this.render();
  }

  private focusDraft(): void {
    this.root.querySelector<HTMLInputElement>('.lg-tagger__label')?.focus();
  }
}

const EMPHASIS_LABEL: Record<TagEmphasis, string> = {
  mention: 'Mention',
  emphasise: 'Emphasise',
  critical: 'Essential',
};

const EMPHASIS_HINT: Record<TagEmphasis, string> = {
  mention: 'Stated once, in passing.',
  emphasise: 'Called out as something the reconstruction must get right.',
  critical: 'Named as essential — the site is wrong without it.',
};

function describeError(error: unknown): string {
  return error instanceof Error ? error.message : 'something went wrong.';
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}
